import type { VMTMetrics } from '../components/charts/VMTChart';
import {
  formatNarrativeMillionMiles,
  formatNarrativeMiles,
  formatNarrativeNumber,
  formatNarrativePercent,
  NARRATIVE_NO_DATA,
} from './narrativeNumberFormat';

export type VmtChartTab = 'total' | 'percapita' | 'comparison';

export interface VMTComparisonMetrics {
  reportingYear: number;
  ldvMiles: number;
  hdvMiles: number;
  totalMiles: number;
  ldvSharePct: number;
  hdvSharePct: number;
  trendBaselineYear: number | null;
  ldvChangePctSinceBaseline: number | null;
  hdvChangePctSinceBaseline: number | null;
}

export interface VmtKpiSpec {
  value: string;
  label: string;
  color: string;
}

export interface VmtOverviewCopy {
  title: string;
  description: string;
  bodyParagraphs: string[];
  keyInsights: string[];
}

export function vmtOverviewChartIdForTab(tab: VmtChartTab): string {
  if (tab === 'percapita') return 'vmt_percapita';
  if (tab === 'comparison') return 'vmt_comparison';
  return 'vehicle_miles';
}

export function computeVMTComparisonMetrics(
  points: Array<{ year: number; ldv: number; hdv: number | null; total: number }>
): VMTComparisonMetrics | null {
  // Require LDV; HDV must be observed (null = missing EMFAC row, not the same as zero miles).
  const valid = points.filter((p) => p.ldv > 0 && p.hdv !== null);
  if (!valid.length) return null;

  const reporting = valid.reduce((a, b) => (a.year > b.year ? a : b));
  const reportingHdv = reporting.hdv ?? 0;
  const baselineCandidate =
    valid.find((p) => p.year === 2005) ?? valid.reduce((a, b) => (a.year < b.year ? a : b));

  const baselineHdv = baselineCandidate?.hdv;
  const ldvChangePctSinceBaseline =
    baselineCandidate && baselineCandidate.ldv > 0 && baselineCandidate.year !== reporting.year
      ? ((reporting.ldv - baselineCandidate.ldv) / baselineCandidate.ldv) * 100
      : null;
  const hdvChangePctSinceBaseline =
    baselineCandidate &&
    baselineCandidate.ldv > 0 &&
    baselineHdv != null &&
    baselineHdv > 0 &&
    baselineCandidate.year !== reporting.year
      ? ((reportingHdv - baselineHdv) / baselineHdv) * 100
      : null;

  const totalMiles = reporting.ldv + reportingHdv;
  const ldvSharePct = totalMiles > 0 ? (reporting.ldv / totalMiles) * 100 : 0;
  const hdvSharePct = totalMiles > 0 ? (reportingHdv / totalMiles) * 100 : 0;

  return {
    reportingYear: reporting.year,
    ldvMiles: Math.round(reporting.ldv),
    hdvMiles: Math.round(reportingHdv),
    totalMiles: Math.round(totalMiles),
    ldvSharePct: Math.round(ldvSharePct * 10) / 10,
    hdvSharePct: Math.round(hdvSharePct * 10) / 10,
    trendBaselineYear: baselineCandidate?.year ?? null,
    ldvChangePctSinceBaseline:
      ldvChangePctSinceBaseline != null ? Math.round(ldvChangePctSinceBaseline * 10) / 10 : null,
    hdvChangePctSinceBaseline:
      hdvChangePctSinceBaseline != null ? Math.round(hdvChangePctSinceBaseline * 10) / 10 : null,
  };
}

function formatDailyMiles(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return NARRATIVE_NO_DATA;
  return `${formatNarrativeNumber(value)} mi/day`;
}

/** For QA tests — word count of narrative prose. */
export function countNarrativeWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function totalTabHistoricalChange(metrics: VMTMetrics): {
  baselineYear: number | null;
  pct: number | null;
} {
  // Match buildVmtKeyMetrics(total): prefer 2005 baseline when that series exists.
  if (metrics.vmt2005 != null) {
    return { baselineYear: 2005, pct: metrics.changeSince2005 ?? null };
  }
  if (metrics.vmt2020 != null && metrics.changeSince2020 != null) {
    return { baselineYear: 2020, pct: metrics.changeSince2020 };
  }
  return { baselineYear: null, pct: null };
}

function changeDirectionPhrase(pct: number): string {
  return pct >= 0 ? 'an increase' : 'a decrease';
}

export function buildVmtKeyMetrics(
  tab: VmtChartTab,
  location: string,
  metrics: VMTMetrics | null,
  comparison: VMTComparisonMetrics | null
): VmtKpiSpec[] {
  if (tab === 'comparison') {
    if (!comparison) {
      return [
        { value: 'Loading...', label: 'Light-duty VMT', color: '#3b82f6' },
        { value: 'Loading...', label: 'Heavy-duty VMT', color: '#f59e0b' },
        { value: 'Loading...', label: 'LDV share of LDV+HDV', color: '#1e3a8a' },
        { value: 'Loading...', label: 'Reporting year', color: '#64748b' },
      ];
    }
    return [
      {
        value: formatNarrativeMiles(comparison.ldvMiles),
        label: `Light-duty VMT (${comparison.reportingYear})`,
        color: '#3b82f6',
      },
      {
        value: formatNarrativeMiles(comparison.hdvMiles),
        label: `Heavy-duty VMT (${comparison.reportingYear})`,
        color: '#f59e0b',
      },
      {
        value: formatNarrativePercent(comparison.ldvSharePct),
        label: `LDV share of total (${comparison.reportingYear})`,
        color: '#1e3a8a',
      },
      {
        value: formatNarrativePercent(comparison.hdvSharePct),
        label: `HDV share of total (${comparison.reportingYear})`,
        color: '#64748b',
      },
    ];
  }

  if (!metrics) {
    return [
      { value: 'Loading...', label: 'Latest metric', color: '#1e3a8a' },
      { value: 'Loading...', label: 'Comparison', color: '#f59e0b' },
      { value: 'Loading...', label: 'Trend', color: '#10b981' },
      { value: 'Loading...', label: 'Context', color: '#64748b' },
    ];
  }

  if (tab === 'total') {
    const baselineYear = metrics.vmt2005 != null ? 2005 : metrics.vmt2020 != null ? 2020 : null;
    const baselineVmt = baselineYear === 2005 ? metrics.vmt2005 : baselineYear === 2020 ? metrics.vmt2020 : null;
    const pctChange =
      baselineYear === 2005 && metrics.changeSince2005 != null
        ? metrics.changeSince2005
        : metrics.changeSince2020;

    return [
      {
        value: formatNarrativeMillionMiles(metrics.reportingYearLdvVmt),
        label: `Total light-duty VMT (${metrics.reportingYear})`,
        color: '#1e3a8a',
      },
      {
        value:
          pctChange != null
            ? formatNarrativePercent(pctChange, { signed: true })
            : NARRATIVE_NO_DATA,
        label:
          baselineYear != null
            ? `Change since ${baselineYear} (through ${metrics.reportingYear})`
            : 'Change vs baseline',
        color: (pctChange ?? 0) < 0 ? '#10b981' : '#ef4444',
      },
      {
        value:
          baselineVmt != null ? formatNarrativeMillionMiles(baselineVmt) : NARRATIVE_NO_DATA,
        label: baselineYear != null ? `Light-duty VMT in ${baselineYear}` : 'Baseline VMT',
        color: '#64748b',
      },
      {
        value: String(metrics.reportingYear),
        label: 'Latest reporting year (light-duty series)',
        color: '#3b82f6',
      },
    ];
  }

  // percapita
  const caYear = metrics.californiaAverageYear ?? metrics.reportingYear;
  const gap =
    metrics.californiaPerCapitaAvailable && metrics.californiaAverage != null
      ? metrics.currentVMT - metrics.californiaAverage
      : null;

  return [
    {
      value: formatDailyMiles(metrics.currentVMT),
      label: `${location} daily VMT per resident (${metrics.reportingYear})`,
      color: '#1e3a8a',
    },
    {
      value: metrics.californiaPerCapitaAvailable
        ? formatDailyMiles(metrics.californiaAverage)
        : NARRATIVE_NO_DATA,
      label: metrics.californiaPerCapitaAvailable
        ? `California daily average (${caYear})`
        : `California benchmark (${caYear}) unavailable`,
      color: '#f59e0b',
    },
    {
      value:
        gap != null
          ? formatNarrativeNumber(gap, { signed: true, suffix: ' mi/day' })
          : NARRATIVE_NO_DATA,
      label: metrics.californiaPerCapitaAvailable
        ? `Gap vs California (${caYear})`
        : 'Gap vs California',
      color: gap != null && gap < 0 ? '#10b981' : '#ef4444',
    },
    {
      value:
        metrics.changePerCapitaSince2020 != null
          ? formatNarrativePercent(metrics.changePerCapitaSince2020, { signed: true })
          : NARRATIVE_NO_DATA,
      label:
        metrics.changePerCapitaSince2020 != null
          ? `Per-capita change since 2020 (${metrics.reportingYear})`
          : 'Per-capita change since 2020',
      color: (metrics.changePerCapitaSince2020 ?? 0) < 0 ? '#10b981' : '#ef4444',
    },
  ];
}

export function buildVmtOverviewCopy(
  tab: VmtChartTab,
  location: string,
  metrics: VMTMetrics | null,
  comparison: VMTComparisonMetrics | null
): VmtOverviewCopy {
  if (tab === 'comparison') {
    if (!comparison) {
      return {
        title: 'Light-duty vs heavy-duty VMT',
        description:
          'Annual light-duty and heavy-duty vehicle miles traveled for the same community and year.',
        bodyParagraphs: [`Loading comparison data for ${location}...`],
        keyInsights: [],
      };
    }
    const ldvFmt = formatNarrativeMillionMiles(comparison.ldvMiles);
    const hdvFmt = formatNarrativeMillionMiles(comparison.hdvMiles);
    const ldvShare = formatNarrativePercent(comparison.ldvSharePct);
    const hdvShare = formatNarrativePercent(comparison.hdvSharePct);

    const overview = [
      'Transportation emissions come from both everyday passenger driving and heavier commercial traffic.',
      `This view separates annual light-duty vehicle miles traveled (passenger and light commercial vehicles) from heavy-duty truck miles for ${location}, using the same geography and reporting year.`,
      'Understanding the split helps communities target passenger-oriented strategies separately from freight and goods movement.',
    ].join(' ');

    let trendClause =
      ' Longer-term percent changes are not shown because the available light-duty and heavy-duty series do not share a comparable baseline year.';
    if (
      comparison.trendBaselineYear != null &&
      comparison.ldvChangePctSinceBaseline != null &&
      comparison.hdvChangePctSinceBaseline != null &&
      comparison.trendBaselineYear !== comparison.reportingYear
    ) {
      trendClause = ` Since ${comparison.trendBaselineYear}, light-duty miles changed by ${formatNarrativePercent(comparison.ldvChangePctSinceBaseline, { signed: true })} and heavy-duty miles by ${formatNarrativePercent(comparison.hdvChangePctSinceBaseline, { signed: true })}. These patterns may reflect economic activity and logistics as well as local travel, without implying a single cause.`;
    }

    const insights = [
      `In ${comparison.reportingYear}, ${location} recorded about ${ldvFmt} of light-duty VMT and ${hdvFmt} of heavy-duty VMT.`,
      `Light-duty travel accounted for ${ldvShare} of combined on-road miles and heavy-duty for ${hdvShare}.${trendClause}`,
      'Passenger-focused climate strategies (transit, walking, biking, and shorter trips) primarily affect the light-duty share, while freight electrification, routing, and land use near industrial corridors relate to heavy-duty mileage.',
    ].join(' ');

    return {
      title: 'Light-duty vs heavy-duty VMT',
      description: 'Compare passenger light-duty travel with heavy-duty commercial mileage.',
      bodyParagraphs: [overview],
      keyInsights: [insights],
    };
  }

  if (!metrics) {
    return {
      title: 'Vehicle miles traveled',
      description: 'Light-duty vehicle miles traveled for the selected community.',
      bodyParagraphs: [`Loading VMT data for ${location}...`],
      keyInsights: [],
    };
  }

  if (tab === 'total') {
    const latest = formatNarrativeMillionMiles(metrics.reportingYearLdvVmt);
    const { baselineYear, pct } = totalTabHistoricalChange(metrics);

    const overview = [
      'Vehicle miles traveled (VMT) measures driving activity and is a central indicator of transportation-related greenhouse gas emissions.',
      `This chart shows estimated annual light-duty vehicle travel associated with ${location}, including passenger vehicles but excluding heavy-duty trucks.`,
      'Recent travel estimates are combined with historical backcasts so planners can see how total driving has changed over time.',
    ].join(' ');

    let changeClause =
      'A percent change from an earlier baseline year is not available for the current endpoints of this series.';
    if (pct != null && baselineYear != null) {
      changeClause = `That total represents ${changeDirectionPhrase(pct)} of ${formatNarrativePercent(Math.abs(pct))} since ${baselineYear}.`;
    }

    const insights = [
      `${location} recorded about ${latest} of light-duty vehicle miles in ${metrics.reportingYear}. ${changeClause}`,
      'Changes in total VMT may reflect commuting patterns, land use, and transportation choices, although these data alone cannot establish causes.',
      `Tracking this trend helps ${location} evaluate opportunities to reduce driving-related emissions through transit, active transportation, and land use that brings daily destinations closer together.`,
    ].join(' ');

    return {
      title: 'Total light-duty VMT',
      description: 'Annual light-duty vehicle miles traveled for the community.',
      bodyParagraphs: [overview],
      keyInsights: [insights],
    };
  }

  // percapita
  const localDaily = formatDailyMiles(metrics.currentVMT);
  const caYear = metrics.californiaAverageYear ?? metrics.reportingYear;

  const overview = [
    'Per-capita VMT expresses how much daily light-duty driving occurs per resident, which helps compare communities of different sizes on a common basis.',
    `This chart shows average daily light-duty vehicle miles per person for ${location}, using population for the same year as the travel estimate.`,
    'It is useful for equity and quality-of-life conversations because it relates system-wide driving to individual experience.',
  ].join(' ');

  let benchmarkClause: string;
  if (metrics.californiaPerCapitaAvailable) {
    const caDaily = formatDailyMiles(metrics.californiaAverage);
    const gap = metrics.currentVMT - metrics.californiaAverage;
    benchmarkClause = `For ${metrics.reportingYear}, ${location} residents averaged ${localDaily} compared with a California average of ${caDaily} (${formatNarrativeNumber(Math.abs(gap), { suffix: ' mi/day' })} ${gap >= 0 ? 'higher' : 'lower'}).`;
  } else {
    benchmarkClause = `For ${metrics.reportingYear}, ${location} residents averaged ${localDaily}; a same-year California benchmark is not available, so no statewide comparison is shown.`;
  }

  let trendClause = '';
  if (metrics.changePerCapitaSince2020 != null) {
    trendClause = ` Per-capita light-duty VMT ${metrics.changePerCapitaSince2020 >= 0 ? 'rose' : 'fell'} by ${formatNarrativePercent(Math.abs(metrics.changePerCapitaSince2020))} since 2020.`;
  }

  const insights = [
    `${benchmarkClause}${trendClause}`,
    `These patterns support climate planning by showing whether ${location} is moving toward lower passenger-vehicle reliance while preserving mobility for households that depend on driving. Interpretation should consider local context rather than a single policy lever.`,
  ].join(' ');

  return {
    title: 'Per-capita light-duty VMT',
    description: `Daily light-duty miles per resident (${metrics.reportingYear}).`,
    bodyParagraphs: [overview],
    keyInsights: [insights],
  };
}
